/**
 * Shared EditorToolbar Component
 *
 * Toolbar positioned at the top of the editor panel (right side).
 * Contains language selector and save buttons.
 *
 * Layout: [LANGUAGE label + Dropdown] [Spacer] [Save] [Done]
 *
 * Related: OnboardingScreenEditorDialog, ScreenControlPanel
 * Sprint: screen-editor-header (Layout Corrections)
 */

import React from 'react';
import { Box, Button, FormControl, Select, MenuItem, Typography, useTheme } from '@mui/material';
import { getLanguageName, type Language } from '@modules/onboardings/constants/languages';
import {
  LAYOUT_TOOLBAR_HEIGHT,
  SPACING,
  TYPO_LABEL_XS,
  TYPO_BODY,
  TYPO_LETTER_SPACING_LABEL,
} from '../../config/ui';

export interface EditorToolbarProps {
  /** Current locale */
  locale: string;

  /** Available locales for selection */
  availableLocales: string[];

  /** Callback when locale changes */
  onLocaleChange: (locale: string) => void;

  /** Whether there are unsaved changes */
  hasChanges: boolean;

  /** Whether save operation is in progress */
  isSaving: boolean;

  /** Callback when Done (Save & Close) is clicked */
  onSave: () => void;

  /** Callback when Save (only) is clicked - saves without closing */
  onSaveOnly?: () => void;

  /** Whether save button should be disabled */
  saveDisabled?: boolean;
}

/**
 * EditorToolbar - Toolbar for content editor panel
 *
 * Contains:
 * - Language label + selector dropdown (left)
 * - Save button - saves without closing (right)
 * - Done button - saves and closes (right)
 */
export const EditorToolbar: React.FC<EditorToolbarProps> = ({
  locale,
  availableLocales,
  onLocaleChange,
  hasChanges,
  isSaving,
  onSave,
  onSaveOnly,
  saveDisabled = false,
}) => {
  const theme = useTheme();

  const handleLocaleChange = (event: { target: { value: string } }) => {
    onLocaleChange(event.target.value);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: SPACING.lg,
        paddingY: theme.spacing(1.5),
        paddingLeft: theme.spacing(SPACING.lg),
        paddingRight: theme.spacing(SPACING.md),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        minHeight: LAYOUT_TOOLBAR_HEIGHT,
        height: LAYOUT_TOOLBAR_HEIGHT,
      }}
    >
      {/* Left side: Language selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.lg }}>
        {/* Language Selector with Label */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: SPACING.xs }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: TYPO_LABEL_XS,
              fontWeight: 500,
              color: theme.palette.text.secondary,
              textTransform: 'uppercase',
              letterSpacing: TYPO_LETTER_SPACING_LABEL,
            }}
          >
            LANGUAGE
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120, margin: 0 }}>
            <Select
              value={locale}
              onChange={handleLocaleChange}
              displayEmpty
              data-testid="editor-language-select"
              sx={{
                fontSize: TYPO_BODY,
                '& .MuiSelect-select': {
                  padding: 0,
                  paddingRight: '24px !important', // space for dropdown arrow
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            >
              {availableLocales.map((loc) => (
                <MenuItem key={loc} value={loc}>
                  {getLanguageName(loc as Language)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Right side: Save + Done buttons */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        {/* Save (only) - saves without closing */}
        {onSaveOnly && (
          <Button
            variant="outlined"
            color="primary"
            onClick={onSaveOnly}
            disabled={saveDisabled || isSaving || !hasChanges}
            data-testid="editor-save-btn"
          >
            Save
          </Button>
        )}

        {/* Done (Save & Close) - always enabled to allow save even if change detection misses nested updates */}
        <Button
          variant="contained"
          color="primary"
          onClick={onSave}
          disabled={saveDisabled || isSaving}
          data-testid="editor-done-btn"
        >
          {isSaving ? 'Saving...' : 'Done'}
        </Button>
      </Box>
    </Box>
  );
};

export default EditorToolbar;
